/**
 * Shared helpers for approval-card renderers.
 *
 * Kept in a single module so renderer components stay focused on layout.
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react'
import { formatZodError } from './schemas'
import type { z } from 'zod'

// -----------------------------------------------------------------------------
// Text formatting
// -----------------------------------------------------------------------------

/**
 * Convert a `snake_case` or `kebab-case` key into Title Case.
 */
export function snakeToTitleCase(input: string): string {
  if (!input) return input
  return input
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word) => (word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ')
}

/**
 * Render an em dash placeholder for empty-string values.
 */
export const EMPTY_PLACEHOLDER = '---'

export function displayScalar(value: unknown): string {
  if (value === null || value === undefined) return EMPTY_PLACEHOLDER
  if (typeof value === 'string') {
    return value.length === 0 ? EMPTY_PLACEHOLDER : value
  }
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value)
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

// -----------------------------------------------------------------------------
// Validation error display
// -----------------------------------------------------------------------------

/**
 * Inline error block returned by renderers when validation fails.
 */
export interface RendererValidationErrorProps {
  /** Message summarising the schema failure. */
  message: string
  /** The raw input, shown on "Show raw data". */
  rawData: unknown
}

export function RendererValidationError({ message, rawData }: RendererValidationErrorProps) {
  const [showRaw, setShowRaw] = React.useState(false)
  return (
    <div
      role="alert"
      style={sharedStyles.errorContainer}
    >
      <div style={sharedStyles.errorHeader}>
        <AlertTriangle style={sharedStyles.errorIcon} />
        <div style={sharedStyles.errorContent}>
          <p style={sharedStyles.errorMessage}>
            Unable to render this section: {message}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setShowRaw((prev) => !prev)}
        style={sharedStyles.showRawButton}
        className="link-hover"
        aria-expanded={showRaw}
      >
        {showRaw ? 'Hide raw data' : 'Show raw data'}
      </button>
      {showRaw && (
        <pre style={sharedStyles.rawDataPre}>
          {safeStringify(rawData)}
        </pre>
      )}
    </div>
  )
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

// -----------------------------------------------------------------------------
// Disclosure primitive
// -----------------------------------------------------------------------------

export interface DisclosureProps {
  /** Optional label; defaults to "Details". */
  summary: React.ReactNode
  /** Expanded content. */
  children: React.ReactNode
  /** Default open state. Defaults to `false`. */
  defaultOpen?: boolean
  /** Optional controlled open state. */
  open?: boolean
  /** Optional change handler when controlled. */
  onOpenChange?: (open: boolean) => void
  /** Extra class names for the wrapper. */
  className?: string
}

/**
 * Small accessible disclosure used by renderers that need
 * expand/collapse behaviour inside a section.
 */
export function Disclosure({
  summary,
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  className,
}: DisclosureProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const toggle = React.useCallback(() => {
    const next = !open
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }, [open, isControlled, onOpenChange])

  return (
    <div style={sharedStyles.disclosureContainer} className={className}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={sharedStyles.disclosureToggle}
        className="link-hover"
      >
        {open ? (
          <ChevronDown style={sharedStyles.disclosureChevron} aria-hidden="true" />
        ) : (
          <ChevronRight style={sharedStyles.disclosureChevron} aria-hidden="true" />
        )}
        {summary}
      </button>
      {open && <div>{children}</div>}
    </div>
  )
}

// -----------------------------------------------------------------------------
// Star glyph rendering
// -----------------------------------------------------------------------------

/**
 * Render five filled/empty star glyphs for the review reply form renderer.
 * The glyphs are presentational; the numeric rating is exposed via aria-label.
 */
export function StarRating({ rating, max = 5 }: { rating: number; max?: number }) {
  const safeRating = Math.max(0, Math.min(max, Math.floor(rating)))
  return (
    <span
      role="img"
      aria-label={`${safeRating} out of ${max} stars`}
      style={sharedStyles.starRating}
    >
      {'★'.repeat(safeRating)}
      <span style={sharedStyles.emptyStars}>
        {'☆'.repeat(max - safeRating)}
      </span>
    </span>
  )
}

export type ValidationSuccess<T> = { ok: true; data: T }
export type ValidationFailure = { ok: false; message: string }

// -----------------------------------------------------------------------------
// Zod validation runner
// -----------------------------------------------------------------------------

/**
 * Run a zod schema against arbitrary input and return either the parsed data
 * or a formatted failure message. Renderers wrap their entry point with this.
 */
export function validateRendererInput<T>(
  schema: z.ZodType<T>,
  data: unknown,
): ValidationSuccess<T> | ValidationFailure {
  const result = schema.safeParse(data)
  if (result.success) {
    return { ok: true, data: result.data }
  }
  return { ok: false, message: formatZodError(result.error) }
}

const sharedStyles: Record<string, CSSProperties> = {
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    background: 'rgba(239, 68, 68, 0.05)',
    padding: '12px',
  },
  errorHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  errorIcon: {
    marginTop: '2px',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    color: 'var(--error)',
  },
  errorContent: {
    flex: 1,
    fontSize: '14px',
  },
  errorMessage: {
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  showRawButton: {
    alignSelf: 'flex-start',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--accent-primary)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  rawDataPre: {
    maxHeight: '240px',
    overflow: 'auto',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-tertiary)',
    padding: '8px',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
  },
  disclosureContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  disclosureToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    alignSelf: 'flex-start',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--accent-primary)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  disclosureChevron: {
    width: '14px',
    height: '14px',
  },
  starRating: {
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: '-0.5px',
    color: 'var(--warning)',
  },
  emptyStars: {
    color: 'var(--text-muted)',
  },
}
